<template>
  <div class="detalle-oferta-panel">
    <DxTabPanel ref="tabPanel" height="95%" tabs-position="top" icon-position="left" :defer-rendering="true">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" :disabled="false" icon="fa-regular fa-file-lines">
        <oferta-endesa-tab-datos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDatos"></oferta-endesa-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" :disabled="false" icon="fa-regular fa-file-signature">
        <oferta-endesa-tab-plantilla :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabAnexo"></oferta-endesa-tab-plantilla>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" :disabled="false" icon="fa-regular fa-percent">
        <oferta-energia-tab-descuentos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDescuentos"></oferta-energia-tab-descuentos>
      </DxItem>
      <DxItem :key="4" :title="tituloTab4" class="tab-item" :disabled="false" icon="fa-regular fa-euro-sign">
        <oferta-energia-tab-comisiones-panel :model="model" :is-edit-mode="isEditMode" ref="tabComisiones"></oferta-energia-tab-comisiones-panel>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import OfertaEndesaTabDatos from '@/components/ofertas-energia/endesa/OfertaEndesaTabDatos.vue';
import OfertaEndesaTabPlantilla from '@/components/ofertas-energia/endesa/OfertaEndesaTabPlantilla.vue';
import OfertaEnergiaTabDescuentos from '@/components/ofertas-energia/base/OfertaEnergiaTabDescuentos.vue';
import OfertaEnergiaTabComisionesPanel from '@/components/ofertas-energia/base/OfertaEnergiaTabComisionesPanel.vue';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { type CreateComisionEnergiaOfertaModel, type CreateOfertaEndesaModel, type DetailOfertaEndesaModel, type EditOfertaEndesaModel, } from '@/.shared/.api';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import type { DxButton } from 'devextreme-vue';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import validationEngine from 'devextreme/ui/validation_engine';
import { useApi } from '@/.shared/.api/integration';
import { toUTCDateOnly } from '@/.shared/helpers/date-parser-helper';
import { ApiError } from '@/.shared/services/http';
import { OFFER_WITH_BAD_END_DATE } from '@/.shared/errors/codes';

const intl = useIntl();
const api = useApi();
const tabDatos = ref<InstanceType<typeof OfertaEndesaTabDatos>>();
const tabAnexo = ref<InstanceType<typeof OfertaEndesaTabPlantilla>>();
const tabDescuentos = ref<InstanceType<typeof OfertaEnergiaTabDescuentos>>();
const tabComisiones = ref<InstanceType<typeof OfertaEnergiaTabComisionesPanel>>();
const tabPanel = ref<InstanceType<typeof DxTabPanel>>();

const props = defineProps({
  model: {
    type: Object as () => DetailOfertaEndesaModel | CreateOfertaEndesaModel,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  },
  tieneTramitesAsociados: {
    type: Boolean,
    required: true
  }
});

const tituloTab1 = computed(() => `${intl.i18n.t('ofertas.form.datos')}`);
const tituloTab2 = computed(() => `${intl.i18n.t('ofertas.form.plantilla_anexo')}`);
const tituloTab3 = computed(() => `${intl.i18n.t('ofertas.form.descuentos')}`);
const tituloTab4 = computed(() => `${intl.i18n.t('ofertas.form.comisiones')}`);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {

    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const hayDatosLuz = computed(() => {
  return props.model && props.model.DatosLuz != null;
})

const saveOrEdit = async (): Promise<boolean> => {
  if (!await validarFormulario()) {
    toastError(intl.i18n.t('oferta_energia.revise_validacion'));
    return false;
  }

  props.model.FechaInicio = toUTCDateOnly(props.model.FechaInicio);
  props.model.FechaFin = toUTCDateOnly(props.model.FechaFin);

  const esPlantilla = tabAnexo.value?.isPlantillaMode() ?? false;
  prepararDatosAnexo(esPlantilla);

  try {
    if (props.isEditMode) {
      await guardarEdicion(esPlantilla);
    } else {
      await guardarCreacion(esPlantilla);
    }
    return true;
  } catch (e: any) {
    mostrarErrorGuardado(e, props.isEditMode);
    return false;
  }
}

async function validarFormulario(): Promise<boolean> {
  forzarRenderizadoPestanas();

  const resOferta = validationEngine.validateGroup("validacionOfertaEnergia");
  const resTipoPersona = validationEngine.validateGroup("validacionTipoPersona");
  const resFechaFin = validationEngine.validateGroup("validacionFechaFin");
  const resAnexo = await tabAnexo.value?.validateAnexo();
  const resCoordenadas = await tabAnexo.value?.validateCoordenadasFirma();
  const resPotencia = hayDatosLuz.value ? validationEngine.validateGroup("validacionPotenciaMax") : { isValid: true };
  const resDescuentos = await tabDescuentos.value?.validate();

  return (resOferta.isValid && resTipoPersona.isValid && resFechaFin.isValid && !!resAnexo && !!resCoordenadas && resPotencia.isValid && !!resDescuentos)
   ?? false;
}

function forzarRenderizadoPestanas() {
  if (!tabPanel.value) return;
  const instance = tabPanel.value.instance;
  const currentTabIndex = instance.option('selectedIndex');
  instance.option('selectedIndex', 1);
  instance.option('selectedIndex', 2);
  instance.option('selectedIndex', currentTabIndex);
}

function prepararDatosAnexo(esPlantilla: boolean) {
  if (esPlantilla) {
    const anexoData = tabAnexo.value!.getAnexoData();
    props.model.IdAnexoEmpresaServicios = anexoData.idAnexo;
    props.model.VariablesAnexo = anexoData.variables;
  } else {
    props.model.IdAnexoEmpresaServicios = null;
    props.model.VariablesAnexo = null;
  }
}

async function guardarEdicion(esPlantilla: boolean) {
  if (!esPlantilla && documentoASubir.value) {
    try {
      await api.callStandardApi("putAnexoOfertaEnergia", { showLoading: false },
        (props.model as EditOfertaEndesaModel).Id, documentoASubir.value.name, documentoASubir.value);
    } catch {
      toastError(intl.i18n.t('oferta_energia.edicion_anexo_fallo'));
    }
  }

  await api.callStandardApi("editOfertaEndesa", { showLoading: true }, props.model as EditOfertaEndesaModel);
  toastSuccess(intl.i18n.t('oferta_energia.edicion_exito'));
}

async function guardarCreacion(esPlantilla: boolean) {
  let fileName: string | undefined;
  let tempUrl: string | undefined;

  if (!esPlantilla) {
    tempUrl = await api.callStandardApi("postDocumentoTemp", { showLoading: false },
      documentoASubir.value?.name, documentoASubir.value);
    fileName = documentoASubir.value?.name;
  }

  const idOferta = await api.callStandardApi("createOfertaEndesa", { showLoading: true },
    fileName ?? undefined, tempUrl ?? undefined, props.model as CreateOfertaEndesaModel);

  await crearComisiones(idOferta);
  toastSuccess(intl.i18n.t('oferta_energia.creacion_exito'));
}

function mostrarErrorGuardado(e: any, esEdicion: boolean) {
  if (esEdicion && e instanceof ApiError
    && e.details?.errorList.length === 1
    && [OFFER_WITH_BAD_END_DATE].includes(e.details.errorList[0].Code)) {
    toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`));
  } else {
    const clave = esEdicion ? 'oferta_energia.edicion_fallo' : 'oferta_energia.creacion_fallo';
    toastError(intl.i18n.t(clave));
  }
}

const crearComisiones = async (idOferta: string) => {
  const comisiones = tabComisiones.value?.getComisiones();
  if (comisiones && comisiones.length > 0) {
    // Agrupar comisiones por idMarco
    const comisionesPorMarco = comisiones.reduce((acc, comision) => {
      const idMarco = comision.idMarco;
      if (!acc[idMarco]) {
        acc[idMarco] = [];
      }
      acc[idMarco].push(comision);
      return acc;
    }, {} as Record<string, typeof comisiones>);

    // Crear comisiones para cada marco
    for (const [idMarco, comisionesMarco] of Object.entries(comisionesPorMarco)) {
      await api.callStandardApi("createComisionesEnergiaOferta", {showLoading: false}, idOferta, idMarco, comisionesMarco as CreateComisionEnergiaOfertaModel[]);
    }
  }
};

const documentoASubir = computed(() => {
  return tabAnexo.value?.documentoASubir;
});

</script>